import { useRouter } from "next/router";
import Seo from "@/components/common/Seo";
import JsonLd, {
  breadcrumbSchema,
  offerListSchema,
} from "@/components/common/JsonLd";
import CookieBanner from "@/components/CookieBanner";
import Footer from "@/components/common/Footer";
import Header from "@/components/common/Header";
import path from "path";

import PopularBoxesSection from "@/components/PopularBoxesSection";

import WizardCTA from "@/components/sections/WizardCTA";
import CitiesList from "@/components/sections/CitiesList";
import FinalCTA from "@/components/sections/FinalCTA";
import SEOFooterContent from "@/components/sections/SEOFooterContent";

import { promises as fs } from "fs";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { initServerI18n } from "@/lib/initServerI18n";
import { buildPopularBoxes } from "@/utils/buildPopularBoxes";
import { loadCatalog } from "@/lib/catalog";
import { buildRegionalBoxes } from "@/utils/buildPopularBoxes";
import { getRegionalBoxes } from "@/utils/getRegionalBoxes";
import FeatureGridSection from "@/components/sections/FeatureGridSection";
import FaqSection from "@/components/sections/FaqSection";
import HeroSuche from "@/components/homepage/HeroSuche";
import StadtInhalt from "@/components/anlass/StadtInhalt";
import SofortdruckInhalt from "@/components/kategorie/SofortdruckInhalt";
import GuenstigInhalt from "@/components/kategorie/GuenstigInhalt";
import SpiegelInhalt from "@/components/kategorie/SpiegelInhalt";
import VorOrtInhalt from "@/components/kategorie/VorOrtInhalt";
import WeihnachtsfeierInhalt from "@/components/kategorie/WeihnachtsfeierInhalt";
import { anlassAnalyse, ohneBoxen } from "@/utils/anlassAnalyse";
import { druckAnalyse } from "@/utils/druckAnalyse";
import {
  guenstigAnalyse,
  bauformAnalyse,
  vorOrtAnalyse,
} from "@/utils/kategorieAnalyse";
import { stadtFaq } from "@/utils/stadtFaq";
import { getTravelCost } from "@/lib/travelCost";
import { median } from "@/utils/preisAnalyse";

// Seiten, die eine eigene Datei unter /pages haben. Next bedient sie
// ohnehin von dort — sie hier zusätzlich zu erzeugen, kostet nur Bauzeit
// und legt eine zweite, nie ausgelieferte Fassung an.
const EIGENE_SEITEN = new Set([
  "fotobox-mieten-hochzeit",
  "fotobox-mieten-geburtstag",
  "fotobox-mieten-firmenfeier",
]);

export async function getStaticPaths({ locales }) {
  const jsonDirectory = path.join(process.cwd(), "data");
  const paths = [];

  for (const locale of locales) {
    const fileName = locale === "en" ? "locationsUS.json" : "locations.json";
    const filePath = path.join(jsonDirectory, fileName);
    const fileData = await fs.readFile(filePath, "utf8");
    const locations = JSON.parse(fileData);

    for (const loc of locations) {
      if (EIGENE_SEITEN.has(loc.slug)) continue;
      paths.push({
        params: { locationID: loc.slug },
        locale,
      });
    }
  }

  return {
    paths,
    fallback: false, // oder true/‘blocking’ bei häufigen Änderungen
  };
}

export async function getStaticProps({ params, locale }) {
  try {
    const i18n = await initServerI18n(locale);
    const t = i18n.getFixedT(locale, "common");
    const jsonDirectory = path.join(process.cwd(), "data");

    const fileName = locale === "de" ? "locations.json" : "locationsUS.json";
    const locationsPath = path.join(jsonDirectory, fileName);

    const locationsFile = await fs.readFile(locationsPath, "utf8");
    const locations = JSON.parse(locationsFile);
    const locationData = locations.find(
      (loc) => loc.slug === params.locationID
    );

    if (!locationData) {
      return { notFound: true };
    }

    const matchingFooterLinks = locations
      .filter((loc) => loc.footerLinks === locationData.footerLinks)
      .map((loc) => ({
        slug: loc.slug,
        city: loc.city,
      }))
      .sort((a, b) => {
        const field = locale === "de" ? "city" : "state";
        return (a[field] || "").localeCompare(b[field] || "", locale);
      });

    const daySeed = Math.floor(Date.now() / 86400000);
    const isKategorie = locationData.footerLinks === "index";

    // Load popular boxes (Versand-Fotoboxen)
    const json = await loadCatalog();
    const popularBoxes = buildPopularBoxes(json.Photobooths);

    // Load PLZ coordinates for regional box filtering
    const plzPath = path.join(process.cwd(), "public", "plz-coordinates.json");
    const plzRaw = await fs.readFile(plzPath, "utf8");
    const plzCoordinates = JSON.parse(plzRaw || "{}") || {};

    // Load regional boxes (within 60km radius, updated daily)
    const regionalBoxesRaw = getRegionalBoxes(
      json.Photobooths,
      plzCoordinates,
      locationData.zip,
      daySeed,
      60, // radius in km
      3 // number of boxes to return
    );
    const regionalBoxes = buildRegionalBoxes(regionalBoxesRaw);

    // Die Angaben, die es nur für diese Stadt gibt: wer von hier aus liefert,
    // wie weit es ist, was die Anfahrt kostet. Bisher stand auf 190
    // Stadtseiten kein einziger dieser Werte — nur generierter Fließtext.
    // Die Kategorieseiten liefen bis jetzt über dieselbe Vorlage wie die
    // Stadtseiten. /fotobox-mieten-mit-sofortdruck ist nach der Startseite
    // die zweitstärkste Seite der Domain — 13.613 Impressionen in 90 Tagen —
    // und sagte nichts darüber, was Drucken kostet oder wie lange es dauert.
    const KATEGORIE_DATEN = {
      "fotobox-mieten-mit-sofortdruck": druckAnalyse,
      "fotobox-mieten-unter-100-eur": guenstigAnalyse,
      "fotobox-spiegel-mieten": (boxen) => bauformAnalyse(boxen, "Spiegel"),
      "fotobox-mieten-in-der-naehe": vorOrtAnalyse,
      // Wie die Firmenfeier: 100 Gäste, und ohne Ausdrucke nimmt niemand
      // etwas mit.
      "fotobox-mieten-weihnachtsfeier": (boxen) =>
        ohneBoxen(anlassAnalyse(boxen, { gaeste: 100, druckNoetig: true })),
    };
    const berechne = KATEGORIE_DATEN[locationData.slug];
    const kategorieDaten = berechne ? berechne(json.Photobooths) : null;

    // Zahlen für den Suchkopf. Wo die Kategorie eigene hat, gewinnen sie —
    // "53 Fotoboxen mit Sofortdruck" sagt mehr als die Gesamtzahl.
    const alleBoxen = json.Photobooths || [];
    const allePreise = alleBoxen.flatMap((box) =>
      (box.Packages || [])
        .map(
          (paket) =>
            (typeof box.PhotoboothPrice === "number" ? box.PhotoboothPrice : 0) +
            (typeof paket.PackagePrice === "number" ? paket.PackagePrice : 0)
        )
        .filter((preis) => preis > 0)
    );
    const katalog = {
      anzahlBoxen: alleBoxen.length,
      anzahlVersand: alleBoxen.filter((box) => box.PostDelivery).length,
      anzahlRegional: alleBoxen.filter(
        (box) => box.SelfDelivery || box.CustomerPickUp
      ).length,
      abPreis: allePreise.length ? Math.min(...allePreise) : 0,
    };

    let stadtDaten = null;
    if (!isKategorie && locationData.zip) {
      const alleRegionalen = getRegionalBoxes(
        json.Photobooths,
        plzCoordinates,
        locationData.zip,
        daySeed,
        60,
        20
      );

      const preiseVon = (box) =>
        (box.Packages || [])
          .map(
            (paket) =>
              (typeof box.PhotoboothPrice === "number"
                ? box.PhotoboothPrice
                : 0) +
              (typeof paket.PackagePrice === "number" ? paket.PackagePrice : 0)
          )
          .filter((preis) => preis > 0);

      const lokale = [];
      for (const box of alleRegionalen) {
        const preise = preiseVon(box);
        const anfahrt = await getTravelCost({
          partnerZip: box.SelfDeliveryZip || box.CustomerPickUpZip,
          customerZip: locationData.zip,
          ranges: box.ShippingRanges,
        });
        lokale.push({
          name: box.PhotoboothName || box.PartnerName || "Anbieter",
          bauform: box.PhotoboothType || "Fotobox",
          km: anfahrt.known ? anfahrt.distanceKm : null,
          anfahrt: anfahrt.known ? anfahrt.cost : null,
          abPreis: preise.length ? Math.min(...preise) : 0,
          abholung: Boolean(box.CustomerPickUp),
        });
      }

      const versandboxen = (json.Photobooths || []).filter(
        (box) => box.PostDelivery
      );
      const allePreise = [...alleRegionalen, ...versandboxen].flatMap(preiseVon);
      const versandPreise = versandboxen.flatMap(preiseVon);
      const lokalPreise = alleRegionalen.flatMap(preiseVon);

      stadtDaten = {
        lokale: lokale
          .filter((a) => a.abPreis > 0)
          .sort((a, b) => a.abPreis - b.abPreis),
        anzahlVersand: versandboxen.length,
        anzahlPakete: allePreise.length,
        medianGesamt: median(allePreise),
        abPreisVersand: versandPreise.length ? Math.min(...versandPreise) : 0,
        abPreisLokal: lokalPreise.length ? Math.min(...lokalPreise) : 0,
        ohneAnfahrt: lokale.filter((a) => a.anfahrt === 0).length,
        abholung: lokale.filter((a) => a.abholung).map((a) => a.name),
        bauformen: [
          ...new Set(alleRegionalen.map((b) => b.PhotoboothType).filter(Boolean)),
        ],
      };
    }

    return {
      props: {
        locationData,
        matchingFooterLinks,
        productSchemaName: t("productSchema.name"),
        productSchemaDesc: t("productSchema.desc"),
        daySeed,
        popularBoxes,
        regionalBoxes,
        stadtDaten,
        kategorieDaten,
        katalog,
        stand: new Date().toLocaleDateString("de-DE", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        }),
        ...(await serverSideTranslations(locale, ["common"])),
      },
      // Verkürzt von 24 h: Diese Seiten listen Preise. Sie werden nicht
      // gezielt neu erzeugt — 206 Stück wären bei jeder Änderung zu viel —,
      // deshalb muss die Frist kurz genug sein, dass ein geänderter Preis
      // nicht den ganzen Tag falsch dasteht.
// Frist von einer Stunde auf einen Tag (Absprache 2026-10-03):
// Jede Neuerzeugung schreibt die Seite in Vercels Zwischenspeicher, und
// abgerechnet wird in Blöcken zu 8 KB. Eine Seite von ~300 KB kostet so
// rund 36 Einheiten — pro Erzeugung. Stündlich erneuert reicht eine
// Handvoll gecrawlter Seiten, um das Monatskontingent von 200.000
// aufzubrauchen; am 2026-10-03 waren 75 % verbraucht, und bei 100 %
// schaltet Vercel das Projekt ab.
//
// Inhaltlich kostet es nichts: Was sich hier ändert, hängt am Tagesstand
// (daySeed, stand) und wechselt ohnehin nur einmal am Tag.
      revalidate: 86400,
    };
  } catch (error) {
    console.error("❌ Fehler beim Laden der Daten:", error);
    return { notFound: true };
  }
}

/**
 * Der Text, der im Suchergebnis unter dem Titel steht.
 *
 * Vorher stand auf jeder der 190 Stadtseiten dieselbe Kette aus Häkchen —
 * "Druck-Flatrate, Kostenlose Lieferung, Kostenlose Umbuchung, Gutschein".
 * Sie beantwortet keine Suchanfrage und unterscheidet keine Stadt von der
 * anderen; Google schreibt solche Beschreibungen häufig einfach um.
 *
 * Jetzt steht dort, was die Seite für diese Stadt tatsächlich weiß.
 */
function beschreibungFuerStadt(stadt, daten) {
  const teile = [`Fotobox mieten in ${stadt}:`];

  if (daten.lokale.length) {
    teile.push(
      `${daten.lokale.length} ${
        daten.lokale.length === 1 ? "Anbieter" : "Anbieter"
      } aus der Region ab ${daten.abPreisLokal} €`
    );
    teile.push(`und ${daten.anzahlVersand} Versandboxen ab ${daten.abPreisVersand} €.`);
    teile.push("Mit Entfernung, Anfahrtskosten und Liefertermin — jetzt vergleichen.");
  } else {
    teile.push(
      `${daten.anzahlVersand} Versand-Fotoboxen ab ${daten.abPreisVersand} €, Lieferung kostenlos.`
    );
    teile.push("Preise, Druckleistung und Liefertermine im Vergleich.");
  }

  return teile.join(" ");
}

/**
 * Zahlen für den Suchkopf einer Kategorieseite.
 *
 * Jede Analyse hat ihre eigene Form; wo eine Kategorie eigene Zahlen kennt,
 * stehen sie im Kopf, sonst die des ganzen Katalogs. Erfunden wird nichts:
 * Fehlt eine Angabe, bleibt der Platz leer statt falsch.
 */
function heroZahlen(analyse, katalog) {
  const rueckfall = katalog || {};
  if (!analyse) return rueckfall;

  // Sofortdruck
  if (typeof analyse.medianMitDruck === "number") {
    return {
      anzahlBoxen: analyse.anzahlBoxen,
      anzahlVersand: rueckfall.anzahlVersand,
      anzahlRegional: rueckfall.anzahlRegional,
      abPreis: analyse.min,
    };
  }
  // Spiegel und andere Bauformen
  if (analyse.bauform) {
    return {
      anzahlBoxen: analyse.anzahlBoxen,
      anzahlVersand: analyse.perVersand,
      anzahlRegional: analyse.vorOrt,
      abPreis: analyse.min,
    };
  }
  // Anbieter vor Ort
  if (typeof analyse.anzahlVersandBoxen === "number") {
    return {
      anzahlBoxen: analyse.anzahlBoxen + analyse.anzahlVersandBoxen,
      anzahlVersand: analyse.anzahlVersandBoxen,
      anzahlRegional: analyse.anzahlBoxen,
      abPreis: analyse.min,
    };
  }
  // Anlass (Weihnachtsfeier)
  if (typeof analyse.gaeste === "number") {
    return {
      anzahlBoxen: analyse.anzahlBoxen,
      anzahlVersand: analyse.perVersand,
      anzahlRegional: analyse.bauenAuf,
      abPreis: analyse.min,
    };
  }
  // Günstige Angebote: Die Gesamtzahl ist hier die ehrlichere — zwei
  // Pakete unter 100 € im Kopf zu nennen, wäre eine Enttäuschung als
  // Überschrift.
  return { ...rueckfall, abPreis: rueckfall.abPreis };
}

/**
 * Der Kennzahlensatz im Kopf.
 *
 * Der Standardtext endet auf "inklusive Lieferung" — richtig, solange das
 * günstigste Paket eine Versandbox ist. Bei den Spiegelboxen kommt keine
 * per Post; dort liefert der Anbieter und rechnet die Anfahrt nach
 * Entfernung.
 */
function heroHinweis(analyse, katalog) {
  if (!analyse) return null;
  const zahlen = heroZahlen(analyse, katalog);
  if (zahlen.anzahlVersand !== 0) return null;
  return (
    <>
      {zahlen.anzahlBoxen > 0 && (
        <>
          <strong className="font-semibold text-white">
            {zahlen.anzahlBoxen} Fotoboxen
          </strong>{" "}
          im Vergleich
        </>
      )}
      {zahlen.abPreis > 0 && (
        <>
          , ab{" "}
          <strong className="font-semibold text-white">
            {zahlen.abPreis} €
          </strong>
        </>
      )}
      . Geliefert und aufgebaut vom Anbieter; die Anfahrt richtet sich nach
      der Entfernung.
    </>
  );
}

/** Der Satz unter der Überschrift, je Kategorie. */
function heroLead(slug, analyse) {
  const leads = {
    "fotobox-mieten-mit-sofortdruck":
      "Boxen, die vor Ort drucken — mit Kontingent oder Flatrate, vergleichen und direkt buchen.",
    "fotobox-mieten-unter-100-eur":
      "Die günstigsten Angebote im Vergleich — und was für wenig Geld wirklich drin ist.",
    "fotobox-spiegel-mieten":
      "Der Magic Mirror als Blickfang — geliefert und aufgebaut vom Anbieter.",
    "fotobox-mieten-in-der-naehe":
      "Anbieter mit Aufbau vor Ort und Versandboxen für ganz Deutschland.",
    "fotobox-mieten-weihnachtsfeier":
      "Für die Feier im Dezember — mit genug Ausdrucken für die ganze Belegschaft.",
  };
  return leads[slug] || "Vergleichen und direkt buchen.";
}

/** Wählt den Inhalt, der zu dieser Kategorieseite gehört. */
function KategorieInhalt({ slug, analyse, stand }) {
  if (slug === "fotobox-mieten-mit-sofortdruck")
    return <SofortdruckInhalt analyse={analyse} stand={stand} />;
  if (slug === "fotobox-mieten-unter-100-eur")
    return <GuenstigInhalt analyse={analyse} stand={stand} />;
  if (slug === "fotobox-spiegel-mieten")
    return <SpiegelInhalt analyse={analyse} stand={stand} />;
  if (slug === "fotobox-mieten-in-der-naehe")
    return <VorOrtInhalt analyse={analyse} stand={stand} />;
  if (slug === "fotobox-mieten-weihnachtsfeier")
    return <WeihnachtsfeierInhalt analyse={analyse} stand={stand} />;
  return null;
}

export default function LocationPage({
  locationData,
  matchingFooterLinks,
  productSchemaName,
  productSchemaDesc,
  daySeed,
  popularBoxes,
  regionalBoxes,
  stadtDaten,
  kategorieDaten,
  katalog,
  stand,
}) {
  const { locale, isFallback } = useRouter();
  const { t } = useTranslation("common");

  // Unterscheidung zwischen City und Kategorie
  const isCategory = locationData?.footerLinks === "index";

  const relatedCities =
    matchingFooterLinks?.map((city) => ({
      name: city.city.toLowerCase().includes("fotobox")
        ? city.city
        : `Fotobox mieten ${city.city}`,
      href: `/${city.slug}`,
    })) || [];

  // List of available city images (rotate through them)
  const cityImages = [
    "/images/photobooths/51bb50fd153e0c07737fe3b288caa5dc.webp",
    "/images/photobooths/b1f83fffd99afa508385d449bf80c6a3.webp",
    "/images/photobooths/df4d0813a6693cf887b862cb43dc23c9.webp",
  ];

  // Pick an image based on city slug hash
  const getImageForCity = (slug) => {
    const hash = slug
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return cityImages[hash % cityImages.length];
  };

  return (
    <>
      <Seo
        title={locationData?.metaTitle}
        description={
          stadtDaten
            ? beschreibungFuerStadt(locationData?.city, stadtDaten)
            : locationData?.metaDescription
        }
        keywords={locationData?.metaKeywords}
        canonicalPath={`/${locationData?.slug}`}
        noindex={String(locationData?.metaRobots || "").includes("noindex")}
        image={t("metaTag.ogImageUrl")}
      />

      {/* Ohne Breadcrumb kannte Google nur eine flache Wüste aus 200 Städten;
          jetzt ist erkennbar, dass sie unter der Startseite hängen. */}
      <JsonLd
        id="breadcrumb"
        data={breadcrumbSchema(
          [
            { name: "Startseite", path: "/" },
            {
              name: locationData?.title?.orange || locationData?.city,
              path: `/${locationData?.slug}`,
            },
          ],
          locale
        )}
      />
      <JsonLd
        id="stadt-liste"
        data={offerListSchema([...(regionalBoxes || []), ...(popularBoxes || [])], {
          name: `Fotobox mieten in ${locationData?.city}`,
          locale,
          path: `/${locationData?.slug}`,
        })}
      />

      <div className="bg-[#F9F9F9] text-slate-900">
        <Header />

        {isCategory ? (
          <HeroSuche
            titel={locationData?.title?.orange}
            lead={heroLead(locationData?.slug, kategorieDaten)}
            anzahlBoxen={heroZahlen(kategorieDaten, katalog).anzahlBoxen}
            anzahlVersand={heroZahlen(kategorieDaten, katalog).anzahlVersand}
            anzahlRegional={heroZahlen(kategorieDaten, katalog).anzahlRegional}
            abPreis={heroZahlen(kategorieDaten, katalog).abPreis}
            hinweis={heroHinweis(kategorieDaten, katalog)}
          />
        ) : (
          <HeroSuche
            titel={locationData?.title?.orange || `Fotobox mieten in ${locationData?.city}`}
            lead={`Anbieter aus ${locationData?.city} und Versandboxen für ganz Deutschland — vergleichen und direkt buchen.`}
            anzahlBoxen={
              (stadtDaten?.lokale?.length || 0) +
              (stadtDaten?.anzahlVersand || 0)
            }
            anzahlVersand={stadtDaten?.anzahlVersand || 0}
            anzahlRegional={stadtDaten?.lokale?.length || 0}
            abPreis={stadtDaten?.abPreisVersand || 0}
            plzVorgabe={
              locationData?.zip ? String(locationData.zip).padStart(5, "0") : ""
            }
            standardReiter={stadtDaten?.lokale?.length ? "regional" : "versand"}
          />
        )}

        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:py-16 lg:px-8 space-y-16">
          {/* Versand-Fotoboxen Section */}
          {popularBoxes && popularBoxes.length > 0 && (
            <PopularBoxesSection
              id="versand-fotoboxen"
              title={
                isCategory
                  ? locationData?.headlines?.PopularBoxesSection
                  : `Beliebte Versand-Fotoboxen im Vergleich in ${locationData?.city}`
              }
              subtitle={
                isCategory
                  ? `Ausgewählt nach klaren Kriterien wie Ausstattung, Funktionen, Preis-Leistung und Nutzerbewertungen.`
                  : `Fotobox-Versand nach ${locationData?.city}: Anbieter im Vergleich mit Ausstattung, Preisen und Lieferzeiten.`
              }
              items={popularBoxes}
              ctaHref="/"
              ctaLabel="Fotobox mieten im Vergleich"
            />
          )}
          {/* Regional verfügbare Fotoboxen Section */}
          {regionalBoxes && regionalBoxes.length > 0 && (
            <PopularBoxesSection
              id="regional-fotoboxen"
              title={`Fotobox mieten in ${locationData?.city} & Umgebung`}
              subtitle={`Fotobox-Anbieter aus ${locationData?.city} & Umgebung – inklusive Aufbau und persönlichem Ansprechpartner.`}
              items={regionalBoxes}
              ctaHref={`/fotobox-vergleich?delivery=regional&postalCode=${locationData?.zip}`}
              noFollow={true}
              noOpener={true}
              postToHrefOnSeo={true}
            />
          )}
          <WizardCTA
            title={
              isCategory
                ? locationData?.headlines?.WizardCTA
                : `Unsicher, welche Fotobox zu deinem Event in ${locationData?.city} passt?`
            }
          />

          {isCategory ? (
            <>
              {kategorieDaten ? (
                <KategorieInhalt
                  slug={locationData?.slug}
                  analyse={kategorieDaten}
                  stand={stand}
                />
              ) : (
                <FeatureGridSection
                  title={locationData?.headlines?.FeatureGridSectionTrust}
                  subtitle="Wir vergleichen Fotoboxen nach klaren Kriterien – deutschlandweit per Versand."
                  preset="trust"
                />
              )}
              <FaqSection
                title={locationData?.headlines?.FaqSection}
                items={locationData?.faq}
              />
            </>
          ) : (
            <>
              <StadtInhalt
                stadt={locationData?.city}
                daten={stadtDaten}
                stand={stand}
              />
              <FaqSection
                title={`Häufige Fragen zur Fotobox in ${locationData?.city}`}
                subtitle={`Was aus ${locationData?.city} am häufigsten gefragt wird.`}
                items={stadtFaq(locationData?.city, stadtDaten)}
              />
            </>
          )}

          <SEOFooterContent
            title={locationData?.footer?.title}
            htmlContent={locationData?.footer?.details}
          />

          <FinalCTA
            title={
              isCategory
                ? locationData?.headlines?.FinalCTA
                : `Jetzt die passende Fotobox in ${locationData?.city} finden`
            }
          />

          {relatedCities.length > 0 && (
            <CitiesList
              cities={relatedCities}
              title={
                isCategory
                  ? locationData?.headlines?.CitiesList
                  : "Fotobox mieten in weiteren Städten"
              }
            />
          )}
        </main>

        <Footer />
      </div>

      <CookieBanner />
    </>
  );
}
